"use client";

import { motion } from "framer-motion";
import { ClipboardList, Network, Wrench, Rocket } from "lucide-react";

const steps = [
  {
    step: "01",
    title: "Tell us your machine & goals",
    description: "Short form: OS / environment, channels you want, what you want it to do.",
    icon: ClipboardList,
  },
  {
    step: "02",
    title: "We design your topology",
    description: "Decide: local box vs VPS vs hybrid. Plan channels, tools, models, and access patterns.",
    icon: Network,
  },
  {
    step: "03",
    title: "We install, wire, and harden",
    description: "Run Clawbiz install/migration. Configure gateway, channels, skills, security boundaries.",
    icon: Wrench,
  },
  {
    step: "04",
    title: "You live with it",
    description: "You get docs, commands, and patterns for daily use. Optional workshop or follow-up tuning.",
    icon: Rocket,
  },
];

export const ProcessSection = () => {
  return (
    <section id="process" className="relative section-spacing overflow-hidden w-full">
      {/* Background gradient line */}
      <div className="absolute left-1/2 top-[15%] bottom-[15%] w-px -translate-x-1/2 hidden md:block"
        style={{ background: 'linear-gradient(180deg, transparent, hsl(var(--cyan-bright) / 0.2) 30%, hsl(var(--coral-bright) / 0.2) 70%, transparent)' }}
      />

      <div className="section-container relative z-10">
        <div className="max-w-5xl mx-auto space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4"
          >
            <h2 className="text-3xl md:text-5xl font-display font-medium leading-tight">
              How it works
            </h2>
            <p className="text-lg text-muted-foreground">
              From intake to online — nerd-friendly, no drama.
            </p>
          </motion.div>

          <div className="grid gap-5">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className="relative group"
                >
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-[hsl(var(--cyan-bright)/0.1)] to-[hsl(var(--coral-bright)/0.1)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative glass-card rounded-2xl p-6 md:p-8 hover:bg-white/[0.03] transition-all duration-300">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--coral-bright)/0.12)] to-[hsl(var(--cyan-bright)/0.08)] flex items-center justify-center group-hover:from-[hsl(var(--coral-bright)/0.2)] group-hover:to-[hsl(var(--cyan-bright)/0.15)] transition-all duration-300">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono text-sm text-[hsl(var(--cyan-bright))]">{step.step}</span>
                          <h3 className="text-lg md:text-xl font-display font-medium">{step.title}</h3>
                        </div>
                        <p className="text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <p className="font-mono text-sm text-[hsl(var(--cyan-bright)/0.8)]">
              ~ your-ai: online, answering on WhatsApp, Telegram, Discord, Slack …
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
